import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import LocomotiveScroll from 'locomotive-scroll';
import 'locomotive-scroll/dist/locomotive-scroll.css';
import '../styles/locomotive-fixes.css';
import { vehicleService } from '../services/vehicleService';
import InfiniteDraggableGrid from '../components/InfiniteGrid';
import GoogleReviewsSection from '../components/GoogleReviewsSection';

gsap.registerPlugin(ScrollTrigger);

function HomeModern() {
  const containerRef = useRef(null);
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const videoSectionRef = useRef(null);
  const videoRef = useRef(null);
  const videoInnerRef = useRef(null);
  const videoScrollTriggerRef = useRef(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [videoEnded, setVideoEnded] = useState(false);
  const [showSkipBtn, setShowSkipBtn] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const progressRAF = useRef(null);
  const locomotiveScroll = useRef(null);

  // Parallax scroll values
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 1000], [0, -500]);
  const heroScale = useTransform(scrollY, [0, 800], [1, 1.2]);
  const heroOpacity = useTransform(scrollY, [0, 600], [1, 0]);

  // Load vehicles
  useEffect(() => {
    const loadVehicles = async () => {
      console.log('Loading vehicles for homepage...');
      const allVehicles = await vehicleService.getAvailableVehicles();
      console.log('Loaded vehicles:', allVehicles);

      if (allVehicles.data && allVehicles.data.length > 0) {
        // Log first vehicle to see image structure
        console.log('First vehicle images:', allVehicles.data[0].images);

        const galleryData = allVehicles.data.map(vehicle => {
          // Handle both string URLs and object URLs
          const firstImage = vehicle.images?.[0];
          const imageUrl = typeof firstImage === 'string' ? firstImage : firstImage?.url;

          return {
            id: vehicle.id,
            thumb_src: imageUrl || '/assets/assets/images/business-shop-sign.webp',
            full_src: imageUrl || '/assets/assets/images/business-shop-sign.webp',
            title: `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim(),
            price: vehicle.price
          };
        });
        setGalleryItems(galleryData);

        // Filter out vehicles with pending-contract status/tag
        const availableVehicles = allVehicles.data.filter(vehicle => {
          const isPendingContract =
            vehicle.status === 'pending-contract' ||
            vehicle.status === 'pending_contract' ||
            vehicle.status === 'pending' ||
            vehicle.saleStatus === 'pending-contract' ||
            vehicle.saleStatus === 'pending_contract' ||
            vehicle.saleStatus === 'pending' ||
            vehicle.availability === 'pending-contract' ||
            vehicle.availability === 'pending_contract' ||
            vehicle.availability === 'pending' ||
            vehicle.tags?.includes('pending-contract') ||
            vehicle.tags?.includes('pending_contract') ||
            vehicle.tags?.includes('pending') ||
            vehicle.sold === true;

          return !isPendingContract; // Return vehicles that are NOT pending-contract or sold
        });

        // Sort by created_at date (newest first) - assuming created_at field exists
        const sortedVehicles = availableVehicles.sort((a, b) => {
          const dateA = new Date(a.created_at || a.createdAt || 0);
          const dateB = new Date(b.created_at || b.createdAt || 0);
          return dateB - dateA; // Newest first
        });

        setFeaturedVehicles(sortedVehicles.slice(0, 4));
        console.log('Featured vehicles set (most recent, excluding pending-contract):', sortedVehicles.slice(0, 4));
      }
    };

    // Load immediately
    loadVehicles();

    const interval = setInterval(loadVehicles, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // GSAP Animations
  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero title animation - simple version without SplitText
      if (titleRef.current) {
        gsap.from(titleRef.current, {
          opacity: 0.3,
          y: -140,
          scale: .18,
          duration: 1.95,
          ease: "power4.out"
        });

        // Animate each line separately
        const lines = titleRef.current.querySelectorAll('span');
        gsap.from(lines, {
          opacity: 0,
          y: 50,
          stagger: 0.2,
          duration: 1,
          ease: "power3.out"
        });
      }

      // Parallax sections
      gsap.utils.toArray('.parallax-section').forEach(section => {
        const bg = section.querySelector('.parallax-bg');
        if (bg) {
          gsap.to(bg, {
            yPercent: -50,
            ease: ".13s",
            scrollTrigger: {
              trigger: section,
              start: "bottom top",
              end: "bottom top",
              scrub: true,
              scroller: containerRef.current
            }
          });
        }
      });

      // Reveal animations
      gsap.utils.toArray('.reveal-text').forEach(text => {
        gsap.from(text, {
          y: 100,
          opacity: 0,
          duration: .4,
          ease: "power4.out",
          scrollTrigger: {
            trigger: text,
            start: "top 80%"
            // scroller: containerRef.current // Removed for normal scrolling
          }
        });
      });

      // Scale on scroll
      gsap.utils.toArray('.scale-on-scroll').forEach(element => {
        gsap.fromTo(element,
          { scale: 0.8, opacity: 0 },
          {
            scale: 1,
            opacity: 1,
            duration: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: element,
              start: "top 80%"
              // scroller: containerRef.current // Removed for normal scrolling
            }
          }
        );
      });
    });

    return () => ctx.revert();
  }, []);

  // =============================================
  // SCROLL-LOCK VIDEO SECTION
  // =============================================
  const skipOrEndVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (progressRAF.current) cancelAnimationFrame(progressRAF.current);
    setVideoPlaying(false);
    setVideoEnded(true);
    setShowSkipBtn(false);
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';

    // Smoothly scroll past the video section
    if (videoSectionRef.current) {
      const sectionBottom = videoSectionRef.current.getBoundingClientRect().bottom + window.scrollY;
      gsap.to(window, {
        scrollTo: { y: sectionBottom, autoKill: false },
        duration: 0.8,
        ease: 'power2.inOut',
      });
    }
  };

  // GSAP scroll-driven slide-up animation for video section
  useEffect(() => {
    if (!videoSectionRef.current || !videoInnerRef.current || videoEnded) return;

    // Set initial state
    gsap.set(videoInnerRef.current, { y: '100%', opacity: 0, scale: 0.9 });

    const slideUpTrigger = ScrollTrigger.create({
      trigger: videoSectionRef.current,
      start: 'top bottom',    // when section top hits viewport bottom
      end: 'top top',         // when section top hits viewport top
      scrub: 0.6,             // smooth lag for elegant feel
      animation: gsap.to(videoInnerRef.current, {
        y: '0%',
        opacity: 1,
        scale: 1,
        ease: 'none',
      }),
    });

    return () => slideUpTrigger.kill();
  }, [videoEnded]);

  useEffect(() => {
    // Dynamically import ScrollToPlugin
    import('gsap/ScrollToPlugin').then(({ ScrollToPlugin }) => {
      gsap.registerPlugin(ScrollToPlugin);
    });
  }, []);

  useEffect(() => {
    if (!videoSectionRef.current || !videoRef.current || videoEnded) return;

    const video = videoRef.current;

    // Show skip button after 2 seconds of playback
    let skipTimeout;
    const updateProgress = () => {
      if (video.duration) {
        setVideoProgress((video.currentTime / video.duration) * 100);
      }
      progressRAF.current = requestAnimationFrame(updateProgress);
    };
    const handlePlay = () => {
      setVideoPlaying(true);
      skipTimeout = setTimeout(() => setShowSkipBtn(true), 2000);
      progressRAF.current = requestAnimationFrame(updateProgress);
    };

    const handleEnded = () => {
      skipOrEndVideo();
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('ended', handleEnded);

    // Create ScrollTrigger to detect when video section enters viewport
    const trigger = ScrollTrigger.create({
      trigger: videoSectionRef.current,
      start: 'top top',
      onEnter: () => {
        if (videoEnded) return;
        // Lock scroll
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        // Play video
        video.play().catch(() => {
          // Autoplay blocked — show skip immediately and unlock
          setShowSkipBtn(true);
        });
      },
    });

    videoScrollTriggerRef.current = trigger;

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('ended', handleEnded);
      clearTimeout(skipTimeout);
      if (progressRAF.current) cancelAnimationFrame(progressRAF.current);
      if (trigger) trigger.kill();
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [videoEnded]);

  return (
    <div ref={containerRef} className="bg-black text-white min-h-screen relative" style={{ minHeight: '100vh', isolation: 'isolate' }}>
      <Helmet>
        <title>Paradise-Performance | Used Trucks & European Car Repair | Paradise, TX</title>
        <meta name="description" content="Family-owned dealership in Paradise, TX. Quality pre-owned vehicles & European car repair. Serving Dallas-Fort Worth. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/" />
        <meta property="og:title" content="Paradise-Performance | Used Trucks & European Car Repair" />
        <meta property="og:description" content="Family-owned dealership in Paradise, TX. Quality pre-owned vehicles & European car repair. Serving DFW." />
        <meta property="og:image" content="https://paradise-performance.com/og-image.jpg" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="Paradise-Performance | Used Trucks & European Car Repair" />
        <meta property="twitter:description" content="Family-owned dealership in Paradise, TX. Quality pre-owned vehicles & European car repair." />
        <meta property="twitter:image" content="https://paradise-performance.com/og-image.jpg" />
      </Helmet>

      {/* Hero Section - Full Screen */}
      <section
        ref={heroRef}
        className="relative h-screen w-full overflow-hidden isolate"
      >
        <motion.div
          className="absolute inset-0 z-0"
          style={{ y: heroY, scale: heroScale, opacity: heroOpacity }}
        >
          <img
            src="https://media.wired.com/photos/59326c33edfced5820d10421/16:9/w_929,h_523,c_limit/Lamborghini-Models-wide-slow-sloop1.gif"
            alt="Paradise-Performance Lamborghini"
            className="w-full h-full object-cover"
            style={{ position: 'absolute', left: 0 }}
          />
          <div className="absolute inset-0 bg-black/20 pointer-events-none" />
        </motion.div>

        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center px-8">
            <h1
              ref={titleRef}
              className="text-[10vw] font-bold leading-[0.9] mb-6 mix-blend-difference"
              data-scroll
              data-scroll-speed="2"
            >
              <span className="block">PARADISE-</span>
              <span className="block">PERFORMANCE</span>
            </h1>
            <p
              className="text-2xl md:text-3xl font-light tracking-wider opacity-80"
              data-scroll
              data-scroll-speed="1"
            >
              DFW &amp; NORTH TEXAS'S EURO & GERMAN AUTO SPECIALISTS
            </p>
            <p className="text-base md:text-lg font-light tracking-wide opacity-60 mt-2">
              Family-Owned • European Auto Specialists • Pre-Owned Car Dealership
            </p>
            <div className="mt-12 flex gap-6 justify-center">
              <Link
                to="/services"
                className="px-12 py-4 border-2 border-white hover:bg-white hover:text-black transition-all duration-500 text-lg tracking-wider"
              >
                SERVICE
              </Link>
              <Link
                to="/inventory"
                className="px-12 py-4 bg-white text-black hover:bg-transparent hover:text-white border-2 border-white transition-all duration-500 text-lg tracking-wider"
              >
                SALES
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20">
          <div className="w-[1px] h-20 bg-white/30 relative overflow-hidden">
            <div className="absolute top-0 w-full h-full bg-white animate-scrollDown" />
          </div>
        </div>
      </section>

      {/* ========== SCROLL-LOCK VIDEO SECTION ========== */}
      {!videoEnded && (
        <section
          ref={videoSectionRef}
          className="relative w-full bg-black flex items-center justify-center overflow-hidden"
          style={{ height: '100vh' }}
        >
          {/* Inner wrapper — GSAP slides this up as user scrolls */}
          <div
            ref={videoInnerRef}
            className="w-full h-full flex items-center justify-center relative"
          >
            {/* Video — centered, maintains 9:16 ratio */}
            <video
              ref={videoRef}
              className="h-full w-auto max-w-none"
              style={{
                maxHeight: '100vh',
                objectFit: 'contain',
              }}
              playsInline
              preload="metadata"
            >
              <source src="/assets/videos/intro-video.mp4" type="video/mp4" />
            </video>

            {/* Subtle vignette overlay */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.6) 100%)',
              }}
            />

            {/* Skip Video button — appears after 2s */}
            {showSkipBtn && (
              <button
                onClick={skipOrEndVideo}
                className="absolute bottom-8 right-8 z-50 flex items-center gap-2 px-6 py-3 rounded-full text-white text-sm font-medium tracking-wider uppercase transition-all duration-300 hover:scale-105 hover:bg-white/20"
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  animation: 'fadeInUp 0.5s ease-out forwards',
                }}
              >
                Skip Video
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            {/* Progress bar at bottom */}
            {videoPlaying && (
              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/10 z-40">
                <div
                  className="h-full bg-white/60"
                  style={{ width: `${videoProgress}%`, transition: 'width 0.15s linear' }}
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Certification Badges - Minimal with hover tooltips */}
      <section className="py-6 bg-black border-y border-white/5 overflow-visible" data-scroll-section>
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-2 text-sm md:text-base text-gray-500">
            <span className="text-gray-600 uppercase tracking-widest text-xs">Accredited for</span>
            {[
              { name: 'Porsche', info: 'Porsche Partner Network (PPN) — Official accreditation granting access to the Porsche Central Service System (PCSS), guided fault finding (GFS), coding, key programming, and factory support from Stuttgart engineers. Covers modern, classic, hybrid and electric Porsche vehicles.' },
              { name: 'Audi', info: 'Authorized Independent Audi Repair — Access to the Volkswagen Group Retail Portal (GRP) and ODIS diagnostic platform for Audi-specific protocols, complex repairs, OEM parts coordination, and factory technical resources.' },
              { name: 'BMW', info: 'Authorized Independent BMW Repair — Access to BMW diagnostic systems and technical resources for factory-level service and repair.' },
              { name: 'VW', info: 'Authorized Independent Volkswagen Repair — Approved access to the Volkswagen Group Retail Portal (GRP) and ODIS diagnostic tools for advanced diagnostics, coding, and aftersales service programs.' },
              { name: 'Land Rover', info: 'Authorized Independent Land Rover Repair — Access to Land Rover diagnostic systems and technical resources for factory-level service.' },
              { name: 'Jaguar', info: 'Authorized Independent Jaguar Repair — Access to Jaguar diagnostic systems and technical resources for factory-level service.' },
            ].map((brand, i) => (
              <span key={brand.name} className="relative group text-white/70 hover:text-white transition-colors cursor-default">
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 opacity-60 group-hover:opacity-100 transition-opacity duration-200">
                  <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                </span>
                {brand.name}{i < 5 && <span className="text-gray-700 ml-8">/</span>}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-5 w-72 md:w-80 p-4 bg-[#111] border border-white/10 rounded-xl text-xs text-gray-300 leading-relaxed opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none shadow-2xl">
                  <p className="text-white font-semibold text-[11px] uppercase tracking-wider mb-2">{brand.name}</p>
                  <p>{brand.info}</p>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-white/10" />
                </div>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Are - Raw/Industrial Style */}
      <section className="py-24 md:py-32 bg-neutral-950 relative" data-scroll-section>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")' }} />

        <div className="container mx-auto px-6 md:px-8 relative">
          <motion.div
            className="mb-20"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <p className="text-gray-500 uppercase tracking-[0.3em] text-sm mb-4">Paradise, Texas</p>
            <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight">WHO WE ARE</h2>
            <div className="w-24 h-[2px] bg-white/20 mt-8" />
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Main intro text */}
            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="space-y-6">
                <p className="text-lg text-gray-300 leading-relaxed">
                  Paradise-Performance is a family-owned dealership and full-service repair shop in Paradise, TX. We specialize in European vehicles - BMW, Porsche, Audi, Mercedes, Land Rover, Jaguar and VW - but we work on just about anything that rolls into the shop.
                </p>
                <p className="text-lg text-gray-400 leading-relaxed">
                  We're mechanics by trade who also sell cars. That means every vehicle on our lot gets inspected and serviced before it hits the front line.
                </p>
                <p className="text-lg text-gray-400 leading-relaxed">
                  Customers drive out from Dallas, Fort Worth, Denton and across North Texas because they're tired of the dealership runaround. Here, you'll talk directly with the people who work on the cars. No games, no hidden fees - the price you see is the price you pay.
                </p>
              </div>
            </motion.div>

            <div className="lg:col-span-7 space-y-4 lg:mt-[-20vh] md:mt-[0vh]">
              <motion.div
                className="group border-l-2 border-blue-500/40 hover:border-white/40 pl-6 py-4 transition-all duration-300"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1, duration: 0.6 }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span className="text-gray-600 text-xs uppercase tracking-wider">01</span>
                    <h3 className="text-xl md:text-2xl font-semibold mt-1 group-hover:text-white transition-colors">Pre-Owned Vehicle Sales</h3>
                    <p className="text-gray-500 mt-2 max-w-lg">Although our inventory isn't huge, we're proud to sell high-quality pre-owned vehicles that are not only safe to drive - but that give you some style while on the road.</p>
                  </div>
                  <Link to="/inventory" className="text-gray-600 hover:text-white text-2xl transition-colors flex-shrink-0">→</Link>
                </div>
              </motion.div>

              <motion.div
                className="group border-l-2 border-white/10 hover:border-white/40 pl-6 py-4 transition-all duration-300"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1, duration: 0.6 }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span className="text-gray-600 text-xs uppercase tracking-wider">01</span>
                    <h3 className="text-xl md:text-2xl font-semibold mt-1 group-hover:text-white transition-colors">European Auto Repair</h3>
                    <p className="text-gray-500 mt-2 max-w-lg">Expert techs for Porsche, Audi, BMW, VW, Land Rover, Jaguar. Dealer quality, not dealer prices.</p>
                  </div>
                  <Link to="/services/european-car-repair" className="text-gray-600 hover:text-white text-2xl transition-colors flex-shrink-0">→</Link>
                </div>
              </motion.div>

              {/* Buy Here Pay Here */}
              <motion.div
                className="group border-l-2 border-amber-500/30 hover:border-amber-500/60 pl-6 py-4 transition-all duration-300 mt-8"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="text-amber-500/60 text-xs uppercase tracking-wider">02</span>
                      <span className="text-amber-500/80 text-[10px] uppercase tracking-wider border border-amber-500/30 px-2 py-0.5">Coming Soon</span>
                    </div>
                    <h3 className="text-xl md:text-2xl font-semibold mt-1 text-gray-400 group-hover:text-amber-400 transition-colors">Buy Here Pay Here</h3>
                    <p className="text-gray-600 mt-2 max-w-lg">Expanding to in-house financing. Bad credit, no credit - working on it.</p>
                  </div>
                  <Link to="/finance-application" className="text-gray-700 hover:text-amber-500 text-2xl transition-colors flex-shrink-0">→</Link>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Vehicles - Fixed Grid */}
      <section className="py-16 mb-12 sm:py-24 md:py-12 pb-24 sm:pb-32 md:pb-20 relative bg-black isolate" data-scroll-section>
        <div className="relative z-20">
          <div className="px-4 sm:px-6 md:px-8 mb-8 sm:mb-12 md:mb-16">
            <h2
              className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold reveal-text text-white"
              data-scroll
              data-scroll-speed="1"
            >
              FEATURED
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-400 mt-2 sm:mt-3 md:mt-4">Most recently added</p>
          </div>

          <div
            className="flex flex-wrap justify-center gap-4 px-4 sm:px-6 md:px-8 pb-8 sm:pb-10 md:pb-12 overflow-hidden"
            data-scroll
          >
            {featuredVehicles.length > 0 ? featuredVehicles.map((vehicle, index) => {
              // Handle both string URLs and object URLs
              const firstImage = vehicle.images?.[0];
              const imageUrl = typeof firstImage === 'string' ? firstImage : firstImage?.url;
              const displayImage = imageUrl || '/assets/assets/images/business-shop-sign.webp';

              const vehicleLink = `/vehicle/${vehicle.id}`;

              return (
                <motion.div
                  key={vehicle.id}
                  className="relative group cursor-pointer flex-shrink-0 w-full sm:w-[calc(50%-0.5rem)] lg:w-[20vw] max-w-[400px] sm:max-w-none"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.4 }}
                  onClick={() => window.location.href = vehicleLink}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-gray-900 rounded-lg relative">
                    <img
                      src={displayImage}
                      alt={`${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      onError={(e) => {
                        console.error('Image failed to load:', displayImage);
                        e.target.src = '/assets/assets/images/business-shop-sign.webp';
                      }}
                    />
                    <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded font-semibold">
                      NEW
                    </div>
                  </div>
                  <div className="mt-3 text-white">
                    <h3 className="text-lg font-semibold truncate">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </h3>
                    <p className="text-gray-400 text-base">
                      ${vehicle.price?.toLocaleString() || 'Call for price'}
                    </p>
                  </div>
                </motion.div>
              );
            }) : (
              <div className="text-gray-400 text-xl">Loading vehicles...</div>
            )}
          </div>
        </div>
      </section>

      {/* Services Grid - Bento Style */}
      <section className="relative py-32 isolate" data-scroll-section>
        <style>{`
          .service-card {
            transform: translateZ(0);
            will-change: transform, opacity;
            backface-visibility: hidden;
            -webkit-backface-visibility: hidden;
            perspective: 1000px;
            -webkit-font-smoothing: antialiased;
          }
          
          .service-card-inner {
            transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1), 
                        background 0.6s cubic-bezier(0.23, 1, 0.32, 1);
            transform: scale(1) translateZ(0);
          }
          
          .service-card:hover .service-card-inner {
            transform: scale(1.02) translateZ(0);
            background: rgba(255, 255, 255, 0.1);
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .fade-in-up {
            animation: fadeInUp 0.8s cubic-bezier(0.23, 1, 0.32, 1) forwards;
            opacity: 0;
          }
        `}</style>

        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black opacity-90" />
        </div>

        <div className="relative z-10 px-8">
          <motion.h2
            className="text-6xl md:text-8xl font-bold mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
          >
            SERVICES
          </motion.h2>

          <div className="grid grid-cols-12 gap-4">
            <motion.div
              className="col-span-12 md:col-span-6 service-card h-[50vh]"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.23, 1, 0.32, 1] }}
            >
              <Link to="/inventory" className="block h-full">
                <div className="service-card-inner h-full relative overflow-hidden p-8 sm:p-12 md:p-16 flex items-center justify-center rounded-lg border border-white/10 hover:border-white/30 transition-all duration-300 cursor-pointer">
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: 'url(/assets/assets/images/homepage-squares/vehicle-service.webp)',
                      filter: 'brightness(1.2)'
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/40" />
                  <div className="relative z-10 text-center">
                    <h3 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-3 md:mb-6 tracking-wider text-white drop-shadow-2xl">SALES</h3>
                    <p className="text-base sm:text-xl md:text-2xl text-gray-200 max-w-2xl mx-auto drop-shadow-lg">Premium vehicles, exceptional service</p>
                  </div>
                </div>
              </Link>
            </motion.div>

            <motion.div
              className="col-span-12 md:col-span-6 service-card h-[50vh]"
              style={{
                backgroundImage: 'url(/assets/assets/images/homepage-squares/buy.webp)',
                filter: 'brightness(1)'
              }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.23, 1, 0.32, 1] }}
            >
              <Link to="/financing" className="block h-full">
                <div className="service-card-inner h-full bg-gradient-to-r from-emerald-900/40 to-green-900/30 backdrop-blur p-8 sm:p-12 md:p-16 flex items-center justify-center rounded-lg border border-white/10 hover:border-white/30 transition-all duration-300 cursor-pointer">
                  <div className="text-center">
                    <h3 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-3 md:mb-6 tracking-wider text-white">FINANCING</h3>
                    <p className="text-base sm:text-xl md:text-2xl text-gray-200 max-w-2xl mx-auto">Tailored solutions</p>
                  </div>
                </div>
              </Link>
            </motion.div>

            <motion.div
              className="col-span-12 md:col-span-6 service-card h-[50vh]"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.23, 1, 0.32, 1] }}
            >
              <Link to="/services" className="block h-full">
                <div className="service-card-inner h-full relative overflow-hidden p-8 sm:p-12 md:p-16 flex items-center justify-center rounded-lg border border-white/10 hover:border-white/30 transition-all duration-300 cursor-pointer">
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: 'url(/assets/assets/images/homepage-squares/service.webp)',
                      filter: 'brightness(0.8)'
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/40" />
                  <div className="relative z-10 text-center">
                    <h3 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-3 md:mb-6 tracking-wider text-white drop-shadow-2xl">SERVICE</h3>
                    <p className="text-base sm:text-xl md:text-2xl text-gray-200 max-w-2xl mx-auto drop-shadow-lg">Expert maintenance</p>
                  </div>
                </div>
              </Link>
            </motion.div>

            <motion.div
              className="col-span-12 md:col-span-6 service-card h-[50vh]"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.4, ease: [0.23, 1, 0.32, 1] }}
            >
              <Link to="/trade-in" className="block h-full">
                <div className="service-card-inner h-full relative overflow-hidden p-8 sm:p-12 md:p-16 flex items-center justify-center rounded-lg border border-white/10 hover:border-white/30 transition-all duration-300 cursor-pointer">
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: 'url(/assets/assets/images/homepage-squares/trade-in.webp)',
                      filter: 'brightness(0.8)'
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-blue/40" />
                  <div className="relative z-10 text-center">
                    <h3 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-3 md:mb-6 tracking-wider text-white drop-shadow-2xl">TRADE-IN</h3>
                    <p className="text-base sm:text-xl md:text-2xl text-gray-200 max-w-2xl mx-auto drop-shadow-lg">Best value</p>
                  </div>
                </div>
              </Link>
            </motion.div>

          </div>
        </div>
      </section>
      {/* Stats Section - Animated Counters */}
      <section className="py-8 px-16" data-scroll-section>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-16">
          {[
            { number: '400+', label: 'VEHICLES SOLD' },
            { number: 'PPN', label: 'Porsche Partner Network' },
            { number: '100%', label: 'SATISFACTION' },
            { number: '24/7', label: 'SUPPORT' },
          ].map((stat, index) => (
            <div
              key={index}
              className="text-center"
              data-scrolll
              data-scroll-speed={1 + index * 0.92}
            >
              <div className="text-6xl md:text-7xl font-bold mb-2">{stat.number}</div>
              <div className="text-gray-400 tracking-widest">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Gallery - Full Width Infinite Scroll */}
      <section className="relative overflow-hidden" data-scroll-section>
        <div className="py-32">
          <div className="px-2 mb-16">
            <h2 className="text-6xl md:text-8xl font-bold reveal-text">COLLECTION</h2>
          </div>
          <InfiniteDraggableGrid gallery={galleryItems} />
        </div>
      </section>

      {/* Google Reviews — Light trust-building section */}
      <GoogleReviewsSection />

      {/* Contact CTA - Split Screen */}
      <section className="h-screen relative" data-scroll-section>
        <div className="grid grid-cols-1 md:grid-cols-2 h-full">
          <div className="bg-black text-white p-16 flex items-center">
            <div>
              <h2 className="text-6xl font-bold mb-8">LET'S TALK</h2>
              <p className="text-2xl mb-12 text-gray-700">
                Ready to experience automotive excellence?
              </p>
              <Link
                to="/contact"
                className="inline-block px-12 py-4 bg-black text-white hover:bg-gray-900 transition-colors duration-500 text-lg tracking-wider"
              >
                GET IN TOUCH
              </Link>
            </div>
          </div>
          <div className="relative overflow-hidden">
            <img
              class="image-shadow"
              src="public/assets/assets/images/new/ph1.webp"
              alt="Contact"
              className="w-full h-full object-cover"
              data-scroll
              data-scroll-speed="-1"
            />
            <div className="absolute inset-0 bg-black/30" />
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomeModern;
